import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
} from "@/store/slices/adminSlice";
import adminService, {
  resetAdminSessionHandler,
} from "@/services/adminService";

interface AdminCredentials {
  email: string;
  password: string;
}

interface AdminOTPVerification {
  email: string;
  otpCode: string;
}

interface AdminPasswordReset {
  email: string;
  otp: string;
  newPassword: string;
}

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin";
  createdAt: string;
  updatedAt: string;
}

interface AdminSessionData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface AdminAuthHook {
  isAdminAuthenticated: boolean;
  adminUser: AdminUser | null;
  verifyCredentialsAndRequestOTP: (
    credentials: AdminCredentials,
  ) => Promise<{ success: boolean; adminId?: string; otp?: string }>;
  verifyOTPAndSignIn: (otpData: AdminOTPVerification) => Promise<void>;
  requestPasswordReset: (
    email: string,
  ) => Promise<{ success: boolean; otp?: string }>;
  verifyPasswordResetOTP: (resetData: AdminPasswordReset) => Promise<void>;
  logoutAdmin: () => void;
  checkAdminSession: () => Promise<boolean>;
  checkSessionStatus: () => Promise<{
    isValid: boolean;
    adminData?: AdminSessionData;
  }>;
  isLoading: boolean;
  error: string | null;
  otpSent: boolean;
  currentEmail: string | null;
  passwordResetOtpSent: boolean;
  resetEmail: string | null;
}

// Admin API base URL
const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;

export const useAdminAuth = (): AdminAuthHook => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [passwordResetOtpSent, setPasswordResetOtpSent] = useState(false);
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get admin state from Redux
  const { isAuthenticated: isAdminAuthenticated, adminUser } = useAppSelector(
    (state) => state.admin,
  );

  const verifyCredentialsAndRequestOTP = useCallback(
    async (credentials: AdminCredentials) => {
      try {
        setIsLoading(true);
        setError(null);
        setOtpSent(false);
        dispatch(loginStart());

        // Step 1: Verify credentials and request OTP
        const response = await fetch(`${ADMIN_API_BASE}/verify-credentials`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.message || "Erreur de vérification des identifiants",
          );
        }

        if (data.success) {
          setOtpSent(true);
          setCurrentEmail(credentials.email);
          return {
            success: true,
            adminId: data.adminId,
            otp: data.otp, // Only in development
          };
        } else {
          throw new Error("Échec de la vérification des identifiants");
        }
      } catch (err) {
        console.error("Erreur de vérification admin:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Erreur de vérification";
        setError(errorMessage);
        dispatch(loginFailure(errorMessage));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  const verifyOTPAndSignIn = useCallback(
    async (otpData: AdminOTPVerification) => {
      try {
        setIsLoading(true);
        setError(null);

        // Step 2: Verify OTP and complete login
        const response = await fetch(`${ADMIN_API_BASE}/verify-otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: otpData.email,
            otpCode: otpData.otpCode,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Code OTP invalide ou expiré");
        }

        if (data.admin) {
          const adminUser: AdminUser = {
            id: data.admin.id,
            email: data.admin.email,
            firstName: data.admin.firstName,
            lastName: data.admin.lastName,
            role: "admin",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          setOtpSent(false);
          setCurrentEmail(null);
          dispatch(loginSuccess(adminUser));
          resetAdminSessionHandler();
        }
      } catch (err) {
        console.error("Erreur de vérification OTP admin:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Erreur de vérification OTP";
        setError(errorMessage);
        dispatch(loginFailure(errorMessage));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setPasswordResetOtpSent(false);

      const result = await adminService.requestPasswordReset(email);

      if (result.success) {
        setPasswordResetOtpSent(true);
        setResetEmail(email);
        return {
          success: true,
          otp: result.otp, // Only in development
        };
      } else {
        throw new Error("Échec de la demande de réinitialisation");
      }
    } catch (err) {
      console.error("Erreur de demande de réinitialisation:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Erreur de demande de réinitialisation";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyPasswordResetOTP = useCallback(
    async (resetData: AdminPasswordReset) => {
      try {
        setIsLoading(true);
        setError(null);

        const result = await adminService.verifyPasswordResetOTP({
          email: resetData.email,
          otp: resetData.otp,
          newPassword: resetData.newPassword,
        });

        if (result.success) {
          setPasswordResetOtpSent(false);
          setResetEmail(null);
          // Password reset successful - user needs to login with new password
          return;
        } else {
          throw new Error("Échec de la réinitialisation du mot de passe");
        }
      } catch (err) {
        console.error("Erreur de réinitialisation du mot de passe:", err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Erreur de réinitialisation du mot de passe";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logoutAdmin = useCallback(async () => {
    try {
      // Call backend logout endpoint
      await fetch(`${ADMIN_API_BASE}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Erreur de déconnexion admin:", err);
    } finally {
      // Clear local state regardless of backend call result
      setError(null);
      setOtpSent(false);
      setCurrentEmail(null);
      dispatch(logout());
      navigate("/admin");
    }
  }, [navigate, dispatch]);

  const checkAdminSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/me`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        // Session expired or invalid - clear Redux state
        if (isAdminAuthenticated) {
          dispatch(logout());
        }
        return false;
      }

      const data = await response.json();
      if (data.id && data.email) {
        // Session is valid - sync with Redux if needed
        if (!isAdminAuthenticated) {
          const adminUser: AdminUser = {
            id: data.id,
            email: data.email,
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            role: "admin",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          dispatch(loginSuccess(adminUser));
        }
        return true;
      }

      // Invalid session data
      if (isAdminAuthenticated) {
        dispatch(logout());
      }
      return false;
    } catch (err) {
      console.error("Erreur de vérification de la session:", err);
      // Clear Redux state on error if currently authenticated
      if (isAdminAuthenticated) {
        dispatch(logout());
      }
      return false;
    }
  }, [dispatch, isAdminAuthenticated]);

  const checkSessionStatus = useCallback(async (): Promise<{
    isValid: boolean;
    adminData?: AdminSessionData;
  }> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/me`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        return { isValid: false };
      }

      const data = await response.json();
      if (data.id && data.email) {
        return {
          isValid: true,
          adminData: {
            id: data.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
          },
        };
      }

      return { isValid: false };
    } catch (err) {
      console.error("Erreur de vérification du statut de la session:", err);
      return { isValid: false };
    }
  }, []);

  return {
    isAdminAuthenticated,
    adminUser,
    verifyCredentialsAndRequestOTP,
    verifyOTPAndSignIn,
    requestPasswordReset,
    verifyPasswordResetOTP,
    logoutAdmin,
    checkAdminSession,
    checkSessionStatus,
    isLoading,
    error,
    otpSent,
    currentEmail,
    passwordResetOtpSent,
    resetEmail,
  };
};
